import { Button, Card, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, GripVertical, Plus, Save, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  createForm,
  getForm,
  updateForm,
  type FormFieldInput,
  type FormStatus,
} from '../../lib/forms-api'

type FieldType = FormFieldInput['type']

interface DraftField extends FormFieldInput {
  /** Stable local id for React keys while editing. */
  _uid: string
}

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Paragraph' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Multiple choice' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'consent', label: 'Consent checkbox' },
]

const HAS_OPTIONS: ReadonlySet<FieldType> = new Set(['select', 'radio', 'checkbox'])

let uidCounter = 0
const nextUid = () => `f${++uidCounter}`

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120)
}

function keyify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64)
}

export function FormBuilder() {
  const params = useParams<{ id: string }>()
  const isNew = !params.id || params.id === 'new'
  const formId = isNew ? null : params.id!
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<FormStatus>('draft')
  const [consentText, setConsentText] = useState('')
  const [confirmationMessage, setConfirmationMessage] = useState('')
  const [fields, setFields] = useState<DraftField[]>([])
  const [formError, setFormError] = useState<string | null>(null)

  const detailQuery = useQuery({
    queryKey: ['form', formId],
    queryFn: () => getForm(formId!),
    enabled: !!formId,
  })

  useEffect(() => {
    const d = detailQuery.data
    if (!d) return
    setName(d.name)
    setSlug(d.slug)
    setSlugTouched(true)
    setDescription(d.description ?? '')
    setStatus(d.status)
    setConsentText(d.consentText ?? '')
    setConfirmationMessage(d.confirmationMessage ?? '')
    setFields(
      d.fields.map((f) => ({
        _uid: nextUid(),
        key: f.key,
        label: f.label,
        type: f.type,
        helpText: f.helpText,
        placeholder: f.placeholder,
        required: f.required,
        position: f.position,
        config: f.config,
      })),
    )
  }, [detailQuery.data])

  // Auto-derive slug from the name until the user edits the slug directly.
  useEffect(() => {
    if (!slugTouched && isNew) setSlug(slugify(name))
  }, [name, slugTouched, isNew])

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || null,
        status,
        consentText: consentText.trim() || null,
        confirmationMessage: confirmationMessage.trim() || null,
        fields: fields.map(({ _uid, ...f }, i) => ({ ...f, position: i })),
      }
      return formId ? updateForm(formId, payload) : createForm(payload)
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['forms'] })
      void queryClient.invalidateQueries({ queryKey: ['form', saved.id] })
      navigate('/app/forms')
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : 'Could not save the form')
    },
  })

  const duplicateKeys = useMemo(() => {
    const seen = new Set<string>()
    const dupes = new Set<string>()
    for (const f of fields) {
      const k = f.key.trim()
      if (!k) continue
      if (seen.has(k)) dupes.add(k)
      seen.add(k)
    }
    return dupes
  }, [fields])

  const canSave =
    name.trim().length > 0 &&
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim()) &&
    fields.every((f) => f.key.trim() && f.label.trim()) &&
    duplicateKeys.size === 0 &&
    !save.isPending

  function addField() {
    setFields((prev) => [
      ...prev,
      {
        _uid: nextUid(),
        key: '',
        label: '',
        type: 'text',
        helpText: null,
        placeholder: null,
        required: false,
        config: {},
      },
    ])
  }

  function updateField(uid: string, patch: Partial<DraftField>) {
    setFields((prev) => prev.map((f) => (f._uid === uid ? { ...f, ...patch } : f)))
  }

  function removeField(uid: string) {
    setFields((prev) => prev.filter((f) => f._uid !== uid))
  }

  function moveField(uid: string, dir: -1 | 1) {
    setFields((prev) => {
      const idx = prev.findIndex((f) => f._uid === uid)
      const next = idx + dir
      if (idx < 0 || next < 0 || next >= prev.length) return prev
      const copy = [...prev]
      const [item] = copy.splice(idx, 1)
      copy.splice(next, 0, item!)
      return copy
    })
  }

  if (formId && detailQuery.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-muted">
        <Spinner /> Loading form…
      </div>
    )
  }

  if (formId && detailQuery.isError) {
    return (
      <div className="space-y-2 py-20 text-center">
        <p className="text-sm text-danger">Couldn’t load this form.</p>
        <Button asChild variant="secondary">
          <Link to="/app/forms">Back to forms</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isNew ? 'New form' : 'Edit form'}
        description="Define the form and its fields. Publish it to accept public submissions."
        actions={
          <>
            <Button asChild variant="ghost">
              <Link to="/app/forms">
                <ArrowLeft />
                Back
              </Link>
            </Button>
            <Button onClick={() => save.mutate()} disabled={!canSave}>
              {save.isPending ? <Spinner /> : <Save />}
              Save form
            </Button>
          </>
        }
      />

      {formError && (
        <div
          role="alert"
          className="rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger"
        >
          {formError}
        </div>
      )}

      <Card className="space-y-5 p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="form-name">Name</Label>
            <Input
              id="form-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Volunteer sign-up"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="form-slug">Public slug</Label>
            <Input
              id="form-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true)
                setSlug(slugify(e.target.value))
              }}
              placeholder="volunteer-sign-up"
              aria-describedby="form-slug-help"
            />
            <p id="form-slug-help" className="text-xs text-dim">
              Used in the public URL. Lowercase letters, numbers, and dashes.
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="form-desc">Description</Label>
          <textarea
            id="form-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-fg placeholder:text-dim focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
            placeholder="Shown to people filling out the form."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="form-status">Status</Label>
            <select
              id="form-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as FormStatus)}
              className="h-10 w-full rounded-lg border border-border bg-card px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
            >
              <option value="draft">Draft — not accepting</option>
              <option value="published">Published — live</option>
              <option value="closed">Closed — archived</option>
            </select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="form-consent">Consent statement (optional)</Label>
          <textarea
            id="form-consent"
            value={consentText}
            onChange={(e) => setConsentText(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-fg placeholder:text-dim focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
            placeholder="e.g. I consent to being contacted about this request."
          />
          <p className="text-xs text-dim">
            When set, submitters must explicitly agree before submitting.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="form-confirm">Confirmation message (optional)</Label>
          <textarea
            id="form-confirm"
            value={confirmationMessage}
            onChange={(e) => setConfirmationMessage(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-fg placeholder:text-dim focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
            placeholder="Thanks! We’ll be in touch soon."
          />
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">Fields</h2>
        <Button variant="secondary" onClick={addField}>
          <Plus />
          Add field
        </Button>
      </div>

      {fields.length === 0 ? (
        <Card className="py-12 text-center text-sm text-muted">
          No fields yet. Add a field to start building your form.
        </Card>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => (
            <Card key={field._uid} className="space-y-4 p-4">
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center gap-1 pt-1.5 text-dim">
                  <GripVertical className="size-4" aria-hidden="true" />
                  <div className="flex flex-col">
                    <button
                      type="button"
                      aria-label="Move field up"
                      disabled={index === 0}
                      onClick={() => moveField(field._uid, -1)}
                      className="text-xs text-dim hover:text-fg disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      aria-label="Move field down"
                      disabled={index === fields.length - 1}
                      onClick={() => moveField(field._uid, 1)}
                      className="text-xs text-dim hover:text-fg disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                </div>

                <div className="grid flex-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor={`label-${field._uid}`}>Label</Label>
                    <Input
                      id={`label-${field._uid}`}
                      value={field.label}
                      onChange={(e) => {
                        const label = e.target.value
                        const autoKey = !field.key || field.key === keyify(field.label)
                        updateField(field._uid, {
                          label,
                          ...(autoKey ? { key: keyify(label) } : {}),
                        })
                      }}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`type-${field._uid}`}>Type</Label>
                    <select
                      id={`type-${field._uid}`}
                      value={field.type}
                      onChange={(e) =>
                        updateField(field._uid, { type: e.target.value as FieldType })
                      }
                      className="h-10 w-full rounded-lg border border-border bg-card px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
                    >
                      {FIELD_TYPES.map((ft) => (
                        <option key={ft.value} value={ft.value}>
                          {ft.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`key-${field._uid}`}>Field key</Label>
                    <Input
                      id={`key-${field._uid}`}
                      value={field.key}
                      onChange={(e) => updateField(field._uid, { key: keyify(e.target.value) })}
                      placeholder="your_name"
                      className="font-mono"
                      aria-invalid={duplicateKeys.has(field.key.trim())}
                    />
                    {duplicateKeys.has(field.key.trim()) && (
                      <p className="text-xs text-danger">Duplicate key.</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`help-${field._uid}`}>Help text</Label>
                    <Input
                      id={`help-${field._uid}`}
                      value={field.helpText ?? ''}
                      onChange={(e) =>
                        updateField(field._uid, { helpText: e.target.value || null })
                      }
                      placeholder="Optional hint"
                    />
                  </div>

                  {HAS_OPTIONS.has(field.type) && (
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor={`opts-${field._uid}`}>Options (one per line)</Label>
                      <textarea
                        id={`opts-${field._uid}`}
                        rows={3}
                        value={(field.config?.options ?? []).map((o) => o.label).join('\n')}
                        onChange={(e) => {
                          const options = e.target.value
                            .split('\n')
                            .map((l) => l.trim())
                            .filter(Boolean)
                            .map((label) => ({ label, value: keyify(label) || label }))
                          updateField(field._uid, { config: { ...field.config, options } })
                        }}
                        className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-fg placeholder:text-dim focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
                        placeholder={'Option A\nOption B'}
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-2 sm:col-span-2">
                    <input
                      id={`req-${field._uid}`}
                      type="checkbox"
                      checked={field.required ?? false}
                      onChange={(e) => updateField(field._uid, { required: e.target.checked })}
                      className="size-4 rounded border-border accent-[var(--primary)]"
                    />
                    <Label htmlFor={`req-${field._uid}`} className="cursor-pointer">
                      Required
                    </Label>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remove field"
                  onClick={() => removeField(field._uid)}
                >
                  <Trash2 />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
